import { useMemo } from 'react';

import type { InkeepChatButtonProps } from '@inkeep/cxkit-react';
import type { GetHelpOption } from '@inkeep/cxkit-types';
import { colors } from '@truvhq/ui';
import { useLocation } from 'react-router-dom';

import { DashboardEvent } from 'features/dashboard/lib/constants';
import { useGetWidgetSettings } from 'hooks/customization';
import { trackEvent } from 'shared/lib/analytics/trackEvent';
import { useAuth } from 'shared/lib/auth/useAuth';

import { submitHubSpotTicket } from './submitHubSpotTicket';

const CHAT_AVATAR_SVG = `
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="7.5" stroke="black" stroke-width="1.88"/>
    <path d="M7.57 7.5a2.5 2.5 0 0 1 4.86.83c0 1.67-2.5 2.5-2.5 2.5" stroke="black" stroke-width="1.88" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="10" cy="13.75" r="0.42" stroke="black" stroke-width="1.88"/>
  </svg>
`;
const CHAT_AVATAR_DATA_URL = `data:image/svg+xml,${encodeURIComponent(CHAT_AVATAR_SVG)}`;

const CHAT_BUTTON_STYLES = `
  .ikp-chat-button__container {
    top: auto !important;
    bottom: 23px !important;
    right: 23px !important;
  }
  .ikp-chat-button__button {
    background: #FFFFFF !important;
    border-radius: 100px !important;
    padding: 12px 24px !important;
    box-shadow: 0px 6px 20px rgba(0, 0, 0, 0.08) !important;
    outline: 1px solid #E0E0E0 !important;
    outline-offset: -1px !important;
    width: auto !important;
    height: auto !important;
    min-width: unset !important;
    min-height: unset !important;
    gap: 8px !important;
  }
  .ikp-chat-button__text {
    color: #000000 !important;
    font-weight: 500 !important;
    font-size: 16px !important;
    line-height: 24px !important;
    font-family: "SF Pro Text", system-ui, -apple-system, sans-serif !important;
  }
  .ikp-chat-button__avatar-content {
    width: 20px !important;
    height: 20px !important;
    order: -1 !important;
    margin-left: 0 !important;
  }
  .ikp-chat-button__avatar-image {
    width: 20px !important;
    height: 20px !important;
  }
`;

const MODAL_CLOSE_STYLES = `
  @media (min-width: 768px) {
    .ikp-modal__close {
      display: inline-flex !important;
    }
  }
`;

export const useInkeepConfig = (): InkeepChatButtonProps => {
  const { user } = useAuth();
  const { data: widgetSettings } = useGetWidgetSettings({ enabled: !!user?.is_approved });
  const { pathname } = useLocation();
  const userId = user?.id ?? '';
  const userName = user?.name?.trim() ?? '';
  const userEmail = user?.email ?? '';
  const userRole = user?.profile?.role?.id ?? '';
  const companyName = user?.company?.name ?? '';
  const companyType = user?.company?.company_type ?? '';
  const platforms = (user?.company?.platforms ?? []).join(', ');
  const useCase = widgetSettings?.use_case ?? '';

  return useMemo(() => {
    const getHelpOptions: GetHelpOption[] = [
      {
        name: 'Create Support Ticket',
        action: {
          type: 'open_form',
          formSettings: {
            heading: 'Create Support Ticket',
            description: 'Submit a ticket and our support team will get back to you.',
            fields: [
              {
                name: 'fullname',
                label: 'Full Name',
                inputType: 'text',
                isRequired: true,
                isHidden: true,
                defaultValue: userName,
              },
              {
                name: 'email',
                label: 'Email',
                inputType: 'email',
                isRequired: true,
                isHidden: true,
                defaultValue: userEmail,
              },
              {
                name: 'subject',
                label: 'Subject',
                inputType: 'text',
                isRequired: true,
                placeholder: 'Brief summary of your issue',
              },
              {
                name: 'description',
                label: 'Description',
                inputType: 'textarea',
                isRequired: true,
                placeholder: 'Please describe your issue in detail',
              },
              {
                name: 'hs_file_upload',
                label: 'Attachment',
                inputType: 'file',
              },
              {
                _type: 'include_chat_session',
                name: 'include_transcript',
                label: 'Include chat transcript',
                inputType: 'checkbox',
                defaultValue: true,
              },
            ],
            buttons: {
              submit: {
                label: 'Submit Ticket',
                onSubmit: submitHubSpotTicket,
              },
              close: {
                action: 'return_to_chat',
              },
            },
            successView: {
              heading: 'Ticket Created',
              message: 'Your support ticket has been submitted. Our team will respond via email.',
              doneButton: {
                label: 'Return to Chat',
                action: 'return_to_chat',
              },
            },
          },
        },
      },
    ];

    const firstName = userName.split(' ')[0];
    const prompts = [
      useCase ? `The user's use case is "${useCase}".` : null,
      companyName ? `The user belongs to company "${companyName}".` : null,
      companyType ? `The company type is "${companyType}".` : null,
      platforms ? `The company is integrated with these platforms: ${platforms}.` : null,
      userRole ? `The user's role is "${userRole}".` : null,
      firstName ? `The user's first name is "${firstName}". Address them by name when natural.` : null,
      pathname ? `The user is currently on the dashboard page "${pathname}".` : null,
    ].filter((p): p is string => Boolean(p));

    return {
      baseSettings: {
        apiKey: import.meta.env.VITE_APP_INKEEP_DASHBOARD_API_KEY || '',
        primaryBrandColor: colors.primary.main,
        userProperties: {
          id: userId,
          name: userName,
          email: userEmail,
          role: userRole,
          company: companyName,
          companyType,
          platforms,
          useCase,
          currentPage: pathname,
        },
        theme: {
          primaryColors: {
            strong: colors.primary.main,
            stronger: colors.primary.hover,
            strongerLight: colors.primary.text,
            medium: colors.secondary.main,
            light: colors.secondary.hover,
            lighter: colors.grey20,
            textBold: colors.primary.text,
            textSubtle: colors.grey60,
          },
          styles: [
            {
              key: 'custom-chat-button',
              type: 'style' as const,
              value: CHAT_BUTTON_STYLES,
            },
            {
              key: 'custom-modal-close',
              type: 'style' as const,
              value: MODAL_CLOSE_STYLES,
            },
          ],
        },
      },
      modalSettings: {
        onOpenChange: (isOpen) => {
          if (isOpen) {
            trackEvent(DashboardEvent.SUPPORT_CHAT_CLICK, { provider: 'inkeep' });
          }
        },
      },
      aiChatSettings: {
        aiAssistantName: 'Truv AI',
        prompts,
        getHelpOptions,
      },
      avatar: CHAT_AVATAR_DATA_URL,
      label: 'Ask Truv AI',
    };
  }, [userId, userName, userEmail, userRole, companyName, companyType, platforms, useCase, pathname]);
};
